import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { inject, observer } from 'mobx-react'
import {
  LikeFilled,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
  SendOutlined,
  SmileFilled,
  PictureFilled,
  CloseCircleFilled,
  PlayCircleFilled,
} from '@ant-design/icons'
import {
  Dropdown,
  Menu,
  Modal,
  Spin,
  message,
  List,
  Avatar,
  Tabs,
} from 'antd'
import moment from 'moment'
import styled from 'styled-components'
import { apiUrl } from '../../../../config'
import { MentionsInput, Mention } from 'react-mentions'
import UserProfilePopover from '../UserProfilePopover'
import MediaGrid from '../MediaGrid/MediaGrid'
import {
  CommentItemContainer,
  CommentBubble,
  CommentAuthor,
  CommentText,
  CommentActionsRow,
  CommentActionBtn,
  CommentTime,
  CommentMoreBtn,
  colors,
  ReactionsModal,
} from '../../NewsFeedStyled'
import {
  REACTIONS,
  ReactionIcon,
} from '../../constants/newsFeedConstants'

const { TabPane } = Tabs
const { confirm } = Modal

const ReactionPopup = styled.div`
  position: absolute;
  bottom: 100%;
  left: -8px;
  background: #ffffff;
  border-radius: 24px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e4e6eb;
  z-index: 1000;
  transform: translateY(-8px);
  white-space: nowrap;
  animation: reactionPopup 0.2s cubic-bezier(0.18, 0.89, 0.32, 1.28) both;

  @keyframes reactionPopup {
    from {
      opacity: 0;
      transform: translateY(10px) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translateY(-8px) scale(1);
    }
  }
`

const ReactionEmoji = styled.div`
  font-size: 24px;
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.18, 0.89, 0.32, 1.28);
  padding: 4px;
  user-select: none;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.35) translateY(-4px);
    background: #f1f5f9;
  }
`

const getReactionKeyFromUser = user => {
  return (
    user?.reaction ||
    user?.reaction_type ||
    user?.reactionKey ||
    user?.reaction_key ||
    user?.reactionCode ||
    user?.reaction_code ||
    null
  )
}

/** Format time ago */
const formatTimeAgo = dateStr => {
  if (!dateStr) return ''
  const now = moment()
  const date = moment(dateStr)
  const diffMinutes = now.diff(date, 'minutes')
  const diffHours = now.diff(date, 'hours')
  const diffDays = now.diff(date, 'days')
  if (diffMinutes < 1) return 'Vừa xong'
  if (diffMinutes < 60) return `${diffMinutes} phút`
  if (diffHours < 24) return `${diffHours} giờ`
  if (diffDays < 7) return `${diffDays} ngày`
  return date.format('DD/MM/YYYY')
}

/** Format name to Capitalize First Letters */
const formatName = name => {
  if (!name) return ''
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const renderTextWithLinks = text => {
  if (typeof text !== 'string') return text
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = text.split(urlRegex)
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1877f2' }}
          onClick={e => e.stopPropagation()}>
          {part}
        </a>
      )
    }
    return part
  })
}

const renderCommentContent = content => {
  if (!content) return ''

  // Use brackets to delimit mentions safely across backends
  const regex = /(@\[[^\]]+\])/g

  const parts = content.split(regex)
  if (parts.length > 1) {
    return parts.map((part, index) => {
      if (part.startsWith('@[') && part.endsWith(']')) {
        return (
          <span
            key={index}
            style={{
              color: '#050505',
              background: 'rgba(24, 119, 242, 0.12)',
              borderRadius: 12,
              padding: '2px 6px',
              fontWeight: 600,
              cursor: 'pointer',
            }}>
            {part.substring(2, part.length - 1)}
          </span>
        )
      }
      return (
        <React.Fragment key={index}>{renderTextWithLinks(part)}</React.Fragment>
      )
    })
  }

  // Fallback for old greedy formatting if no zero-width space was found
  const oldRegex = /(@[A-ZĐĂÂÊÔƠƯÀẢÃÁẠẰẲẴẮẶẦẨẪẤẬỀỂỄẾỆỒỔỖỐỘỜỞỠỚỢÙỦŨÚỤỪỬỮỨỰỲỶỸÝa-zđăâêôơưàảãáạằẳẵắặầẩẫấậềểễếệồổỗốộờởỡớợùủũúụừửữứựỳỷỹý]+(?:\s+[A-ZĐĂÂÊÔƠƯÀẢÃÁẠẰẲẴẮẶẦẨẪẤẬỀỂỄẾỆỒỔỖỐỘỜỞỠỚỢÙỦŨÚỤỪỬỮỨỰỲỶỸÝa-zđăâêôơưàảãáạằẳẵắặầẩẫấậềểễếệồổỗốộờởỡớợùủũúụừửữứựỳỷỹý]+)*)/g
  const oldParts = content.split(oldRegex)
  return oldParts.map((part, index) => {
    if (part.startsWith('@')) {
      return (
        <span
          key={index}
          style={{ color: '#1877f2', fontWeight: 600, cursor: 'pointer' }}>
          {part.substring(1)}
        </span>
      )
    }
    return (
      <React.Fragment key={index}>{renderTextWithLinks(part)}</React.Fragment>
    )
  })
}

/** Resolve avatar URL */
const resolveAvatarUrl = url => {
  if (!url) return null
  if (url.startsWith('http')) return url
  return `${apiUrl}${url}`
}



const CommentItem = ({
  comment,
  currentUser,
  postId,
  newsFeedStore,
  authenticationStore,
  userStore,
  onReply,
  depth = 0,
  dark = false,
}) => {
  const ink = dark ? '#e4e6eb' : '#050505'
  const muted = dark ? '#b0b3b8' : '#65676b'
  const surface = dark ? '#3a3b3c' : '#f0f2f5'
  const cardBg = dark ? '#242526' : '#ffffff'
  const isOwner = currentUser?.username === comment.user_code
  const avatarSrc = resolveAvatarUrl(comment.avatar_url)

  // Parent State (for Root Comment to coordinate replies)
  const [showReplies, setShowReplies] = useState(false)
  const [isReplying, setIsReplying] = useState(false)
  const [replyTarget, setReplyTarget] = useState(null) // { id, display_name }
  const [replyText, setReplyText] = useState('')
  const [isSendingReply, setIsSendingReply] = useState(false)
  const [likesModalVisible, setLikesModalVisible] = useState(false)
  const [likedUsers, setLikedUsers] = useState([])
  const [loadingLikes, setLoadingLikes] = useState(false)
  const [activeReactionTab, setActiveReactionTab] = useState('ALL')
  const [reactionsList, setReactionsList] = useState([])

  // Emojis states & handlers
  const replyInputRef = useRef(null)

  // Reply attachment state
  const replyFileInputRef = useRef(null)
  const [replyAttachedFile, setReplyAttachedFile] = useState(null)

  const openReplyFilePicker = useCallback(() => {
    if (replyFileInputRef.current) {
      replyFileInputRef.current.click()
    }
  }, [])

  const handleReplyFileSelect = useCallback(e => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 100 * 1024 * 1024) {
      message.error('File đính kèm không được vượt quá 100MB')
      return
    }

    const type = file.type.startsWith('video/') ? 'video' : 'image'
    const previewUrl = URL.createObjectURL(file)

    setReplyAttachedFile({
      file,
      type,
      previewUrl,
    })

    if (replyFileInputRef.current) {
      replyFileInputRef.current.value = ''
    }
  }, [])

  const removeReplyAttachedFile = useCallback(() => {
    if (replyAttachedFile?.previewUrl) {
      URL.revokeObjectURL(replyAttachedFile.previewUrl)
    }
    setReplyAttachedFile(null)
  }, [replyAttachedFile])

  const [showEmojiPicker, setShowEmojiPicker] = useState(false)

  const COMMON_EMOJIS = [
    '👍',
    '❤️',
    '😂',
    '😮',
    '😢',
    '😡',
    '🔥',
    '🎉',
    '👏',
    '🤔',
    '🙌',
    '✔️',
    '💯',
    '🚀',
    '💬',
    '🌸',
    '☀️',
    '🌟',
    '🤝',
    '😭',
  ]

  const insertEmoji = useCallback(emoji => {
    // Always append to end to avoid jumping into mention tag
    setReplyText(prev => prev + emoji)
    setShowEmojiPicker(false)
    setTimeout(() => {
      const input = replyInputRef.current
      if (input) {
        input.focus()
        // Move cursor to end
        try {
          const length = input.value?.length || 0
          input.setSelectionRange(length, length)
        } catch (e) {}
      }
    }, 10)
  }, [])

  useEffect(() => {
    const handleOutsideClick = e => {
      if (showEmojiPicker && !e.target.closest('.nf-reply-emoji-picker')) {
        setShowEmojiPicker(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [showEmojiPicker])

  // Retrieve current user avatar
  const { currentUserAvatar } = authenticationStore || {}
  const currentUserAvatarSrc = currentUserAvatar
    ? URL.createObjectURL(currentUserAvatar)
    : null

  const replyState = newsFeedStore.repliesMap[comment.id]
  const replies = replyState?.replies || []
  const repliesCount = replyState?.totalCount || comment.reply_count || 0
  const hasMoreReplies = replyState
    ? replyState.replies.length < replyState.totalCount
    : true
  const repliesLoading = replyState?.loading || false

  // Memoize media array to prevent MediaGrid re-renders on hover (which changes local state)
  const commentMediaArr = useMemo(() => {
    if (!comment.media_url) return null
    return [
      {
        file_id: comment.media_url,
        file_url: comment.media_url,
        media_type: comment.media_type || 'IMAGE',
      },
    ]
  }, [comment.media_url, comment.media_type])

  // Reactions logic
  const [showReactions, setShowReactions] = useState(false)
  const [localReaction, setLocalReaction] = useState(null)
  const [closeTimeoutId, setCloseTimeoutId] = useState(null)

  const handleMouseEnter = () => {
    if (closeTimeoutId) {
      clearTimeout(closeTimeoutId)
      setCloseTimeoutId(null)
    }
    setShowReactions(true)
  }

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowReactions(false)
    }, 400)
    setCloseTimeoutId(timeout)
  }

  const currentUserReaction = useMemo(() => {
    if (!currentUser?.username || reactionsList.length === 0) return null
    const me = reactionsList.find(
      user =>
        user?.userCode?.toLowerCase() === currentUser.username.toLowerCase() ||
        user?.user_code?.toLowerCase() === currentUser.username.toLowerCase()
    )
    const reactionKey = getReactionKeyFromUser(me)
    return REACTIONS.find(r => r.key === reactionKey) || null
  }, [currentUser, reactionsList])

  const activeReaction = useMemo(() => {
    if (!comment.is_liked) return null
    if (localReaction) return localReaction
    const storedReact = localStorage.getItem(`comment-react-${comment.id}`)
    if (storedReact) {
      const found = REACTIONS.find(r => r.key === storedReact)
      if (found) return found
    }
    if (currentUserReaction) return currentUserReaction
    return null
  }, [comment.is_liked, comment.id, localReaction, currentUserReaction])

  const fetchLikes = useCallback(() => {
    newsFeedStore
      .getCommentLikes(comment.id)
      .then(users => {
        setReactionsList(users)
      })
      .catch(err => {
        console.error('Failed to load comment likes', err)
      })
  }, [comment.id, newsFeedStore])

  useEffect(() => {
    if (comment.like_count > 0) {
      fetchLikes()
    } else {
      setReactionsList([])
    }
  }, [comment.id, comment.like_count, comment.reaction_updated_at, fetchLikes])

  const commentReactions = useMemo(() => {
    if (reactionsList.length === 0) return []

    // Deduplicate by user key to avoid double-counting
    const seen = new Map()
    for (const user of reactionsList) {
      const code = (
        user.userCode ||
        user.user_code ||
        user.username ||
        String(user.id || '')
      )
        .toString()
        .toLowerCase()
      if (!code) continue
      if (seen.has(code)) continue
      seen.set(code, user)
    }

    const counts = {}
    for (const user of seen.values()) {
      let rKey = getReactionKeyFromUser(user)
      if (
        !rKey &&
        (user.userCode || user.user_code) === currentUser?.username
      ) {
        rKey = localStorage.getItem(`comment-react-${comment.id}`) || 'LIKE'
      }
      if (!rKey) rKey = 'LIKE'
      counts[rKey] = (counts[rKey] || 0) + 1
    }

    const mapped = Object.keys(counts).map(key => {
      const reactionObj = REACTIONS.find(r => r.key === key) || REACTIONS[0]
      return { key, count: counts[key], reactionObj }
    })

    mapped.sort((a, b) => b.count - a.count)
    return mapped.slice(0, 3)
  }, [reactionsList, currentUser, comment.id])

  const handleSelectReaction = useCallback(
    reactionKey => {
      const reaction = REACTIONS.find(r => r.key === reactionKey)
      const reactionChanged = activeReaction?.key !== reactionKey
      const shouldLike = !comment.is_liked

      localStorage.setItem(`comment-react-${comment.id}`, reactionKey)
      setLocalReaction(reaction)
      setShowReactions(false)
      if (shouldLike || reactionChanged) {
        newsFeedStore
          .toggleLikeComment(comment.id, postId, comment.parent_id, reactionKey)
          .then(() => {
            fetchLikes()
          })
          .catch(() => {
            message.error('Không thể thực hiện thao tác!')
            setLocalReaction(null)
          })
      } else {
        fetchLikes()
      }
    },
    [
      comment.id,
      comment.is_liked,
      activeReaction?.key,
      postId,
      comment.parent_id,
      newsFeedStore,
      fetchLikes,
    ]
  )

  const handleLikeClick = useCallback(() => {
    if (comment.is_liked) {
      localStorage.removeItem(`comment-react-${comment.id}`)
      setLocalReaction(null)
      newsFeedStore
        .toggleLikeComment(comment.id, postId, comment.parent_id, null)
        .then(() => {
          fetchLikes()
        })
        .catch(() => {
          message.error('Không thể thực hiện thao tác!')
        })
    } else {
      localStorage.setItem(`comment-react-${comment.id}`, 'LIKE')
      setLocalReaction(REACTIONS[0])
      newsFeedStore
        .toggleLikeComment(comment.id, postId, comment.parent_id, 'LIKE')
        .then(() => {
          fetchLikes()
        })
        .catch(() => {
          message.error('Không thể thực hiện thao tác!')
          setLocalReaction(null)
          localStorage.removeItem(`comment-react-${comment.id}`)
        })
    }
  }, [
    comment.id,
    comment.is_liked,
    postId,
    comment.parent_id,
    newsFeedStore,
    fetchLikes,
  ])

  const handleShowLikesModal = useCallback(() => {
    setLikesModalVisible(true)
    setLoadingLikes(true)
    newsFeedStore
      .getCommentLikes(comment.id)
      .then(users => {
        setLikedUsers(users)
      })
      .catch(() => {
        message.error('Không thể lấy danh sách lượt thích!')
      })
      .finally(() => {
        setLoadingLikes(false)
        setActiveReactionTab('ALL')
      })
  }, [comment.id, newsFeedStore])

  const groupedReactions = useMemo(() => {
    const groups = { ALL: [] }
    const seen = new Map()
    for (const user of likedUsers) {
      const code = (
        user.userCode ||
        user.user_code ||
        user.username ||
        String(user.id || '')
      )
        .toString()
        .toLowerCase()
      if (!code) continue
      if (seen.has(code)) continue
      seen.set(code, user)
    }

    for (const user of seen.values()) {
      let rKey = getReactionKeyFromUser(user)
      if (
        !rKey &&
        (user.userCode || user.user_code) === currentUser?.username
      ) {
        rKey = localStorage.getItem(`comment-react-${comment.id}`) || 'LIKE'
      }
      if (!rKey) rKey = 'LIKE'
      const reactionObj = REACTIONS.find(r => r.key === rKey) || REACTIONS[0]
      const enrichedUser = { ...user, reactionKey: rKey, reactionObj }
      groups.ALL.push(enrichedUser)
      if (!groups[rKey]) groups[rKey] = []
      groups[rKey].push(enrichedUser)
    }
    return groups
  }, [likedUsers, currentUser, comment.id])

  const currentTabUsers = groupedReactions[activeReactionTab] || []

  const handleDelete = useCallback(() => {
    confirm({
      title: 'Xác nhận xóa bình luận',
      icon: <ExclamationCircleOutlined />,
      content: 'Bình luận sẽ bị xóa vĩnh viễn. Bạn có chắc chắn?',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await newsFeedStore.deleteComment(
            comment.id,
            postId,
            comment.parent_id
          )
          message.success('Đã xóa bình luận')
        } catch (err) {
          message.error(err?.vi || 'Không thể xóa bình luận!')
        }
      },
    })
  }, [postId, comment.id, comment.parent_id])

  const handleToggleReplies = useCallback(() => {
    if (showReplies) {
      // Đang mở → ẩn đi: reset page về 0 để lần mở tiếp sẽ fetch lại từ server
      const replyState = newsFeedStore.repliesMap[comment.id]
      if (replyState) {
        replyState.page = 0
        replyState.replies = []
        replyState.totalCount = comment.reply_count || 0
        replyState.hasMore = true
        newsFeedStore.repliesMap = { ...newsFeedStore.repliesMap }
      }
      setShowReplies(false)
    } else {
      // Đang ẩn → mở: luôn fetch từ server để lấy đủ dữ liệu
      newsFeedStore.getReplies(comment.id).catch(err => {
        message.error('Không thể tải phản hồi!')
      })
      setShowReplies(true)
    }
  }, [showReplies, comment.id, comment.reply_count, newsFeedStore])

  const loadMoreReplies = useCallback(() => {
    newsFeedStore.getReplies(comment.id).catch(err => {
      message.error('Không thể tải thêm phản hồi!')
    })
  }, [comment.id])

  // Submit inline reply
  const handleSendReply = useCallback(async () => {
    const text = replyText.trim()
    if ((!text && !replyAttachedFile) || isSendingReply) return

    try {
      setIsSendingReply(true)

      let payload
      if (replyAttachedFile) {
        payload = new FormData()
        payload.append('content', text)
        payload.append('parent_id', comment.id)
        payload.append('file', replyAttachedFile.file)
      } else {
        payload = {
          content: text,
          parent_id: comment.id,
        }
      }

      await newsFeedStore.createComment(postId, payload)
      setReplyText('')
      setReplyTarget(null)
      removeReplyAttachedFile()
      setIsReplying(false)
      setShowReplies(true)
    } catch (err) {
      message.error('Không thể gửi phản hồi!')
    } finally {
      setIsSendingReply(false)
    }
  }, [
    replyText,
    isSendingReply,
    replyTarget,
    comment.id,
    postId,
    replyAttachedFile,
    removeReplyAttachedFile,
  ])

  // Reply trigger handler
  const handleReplyClick = useCallback(() => {
    setReplyTarget(comment)
    setShowReplies(true)
    setIsReplying(true)
    const mentionTag = `@[${formatName(comment.display_name)}] `
    setReplyText(prev => (prev.startsWith(mentionTag) ? prev : mentionTag))
    setTimeout(() => {
      const input = replyInputRef.current
      if (!input) return
      input.focus()
      const len = input.value?.length || 0
      input.setSelectionRange(len, len) // ← cursor về cuối
    }, 100) // 100ms để state set xong
  }, [comment])

  return (
    <CommentItemContainer
      id={`comment-${comment.id}`}
      style={{ marginLeft: 0, marginTop: depth > 0 ? 4 : 8 }}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          position: 'relative',
        }}>
        {/* Avatar */}
        <UserProfilePopover
          userCode={comment.user_code}
          displayName={comment.display_name}
          avatarUrl={comment.avatar_url}>
          <div
            style={{
              width: depth > 0 ? 24 : 32,
              height: depth > 0 ? 24 : 32,
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
              zIndex: 1,
              cursor: 'pointer',
            }}>
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  background: colors.primary,
                  color: '#fff',
                  fontSize: depth > 0 ? 10 : 12,
                  fontWeight: 'bold',
                }}>
                {comment.display_name?.charAt(0) || 'U'}
              </span>
            )}
          </div>
        </UserProfilePopover>

        {/* Content Right Side */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            minWidth: 0,
          }}>
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
              maxWidth: '100%',
            }}>
            <CommentBubble
              style={{
                background: surface,
                borderRadius: 16,
                padding: '8px 12px',
                display: 'inline-block',
                maxWidth: '100%',
              }}>
              <UserProfilePopover
                userCode={comment.user_code}
                displayName={comment.display_name}
                avatarUrl={comment.avatar_url}>
                <CommentAuthor
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    marginBottom: 2,
                    cursor: 'pointer',
                    color: ink,
                  }}>
                  {formatName(comment.display_name) || 'Người dùng'}
                </CommentAuthor>
              </UserProfilePopover>
              <CommentText
                style={{
                  fontSize: 13,
                  color: ink,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                }}>
                {renderCommentContent(comment.content)}
              </CommentText>
            </CommentBubble>

            {/* Inline media (ảnh/video đính kèm bình luận) */}
            {commentMediaArr && (
              <div
                style={{
                  marginTop: 6,
                  maxWidth: 240,
                  borderRadius: 12,
                  overflow: 'hidden',
                }}>
                <MediaGrid media={commentMediaArr} />
              </div>
            )}

            {comment.like_count > 0 && (
              <div
                onClick={handleShowLikesModal}
                style={{
                  position: 'absolute',
                  right: -10,
                  bottom: -12,
                  background: '#ffffff', // Clean white background to overlay perfectly on the gray bubble
                  borderRadius: 10,
                  padding: '2px 6px 2px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 11,
                  color: '#65676b',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)', // subtle shadow to float beautifully
                  border: 'none',
                  zIndex: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: 2,
                  }}>
                  {commentReactions.length > 0 ? (
                    commentReactions.map((r, i) => (
                      <span
                        key={r.key}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          marginLeft: i > 0 ? -4 : 0, // overlap effect
                          zIndex: 3 - i,
                          position: 'relative',
                        }}>
                        <ReactionIcon type={r.key} size={14} />
                      </span>
                    ))
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}>
                      <ReactionIcon
                        type={activeReaction ? activeReaction.key : 'LIKE'}
                        size={14}
                      />
                    </span>
                  )}
                </div>
                <span>{comment.like_count}</span>
              </div>
            )}
          </div>

          <CommentActionsRow
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              fontSize: 12,
              marginTop: 4,
              paddingLeft: 4,
            }}>
            <CommentTime style={{ color: muted, fontSize: 12 }}>
              {formatTimeAgo(comment.created_at)}
            </CommentTime>
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}>
              {showReactions && (
                <ReactionPopup>
                  {REACTIONS.map(reaction => (
                    <ReactionEmoji
                      key={reaction.key}
                      onClick={e => {
                        e.stopPropagation()
                        handleSelectReaction(reaction.key)
                      }}
                      title={reaction.label}>
                      <ReactionIcon type={reaction.key} size={28} />
                    </ReactionEmoji>
                  ))}
                </ReactionPopup>
              )}
              <CommentActionBtn
                onClick={handleLikeClick}
                className={comment.is_liked ? 'liked' : ''}
                style={{
                  fontSize: 12,
                  padding: 0,
                  fontWeight: 600,
                  color: activeReaction ? activeReaction.color : muted,
                }}>
                {activeReaction ? activeReaction.label : 'Thích'}
              </CommentActionBtn>
            </div>

            <CommentActionBtn
              onClick={handleReplyClick}
              style={{
                fontSize: 12,
                padding: 0,
                fontWeight: 600,
                color: muted,
              }}>
              Trả lời
            </CommentActionBtn>

            {isOwner && (
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="delete"
                      icon={<DeleteOutlined />}
                      danger
                      onClick={handleDelete}>
                      Xóa
                    </Menu.Item>
                  </Menu>
                }
                trigger={['click']}
                placement="bottomRight">
                <CommentMoreBtn
                  style={{
                    fontSize: 12,
                    color: muted,
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 600,
                  }}>
                  Xóa
                </CommentMoreBtn>
              </Dropdown>
            )}
          </CommentActionsRow>
        </div>
      </div>

      {/* Nested Replies Rendering */}
      {true && (
        <div
          style={{
            marginLeft: depth > 3 ? 4 : 16,
            paddingLeft: depth > 3 ? 8 : 24,
            position: 'relative',
          }}>
          {/* Timeline Connector Line */}
          {(showReplies || repliesCount > 0) && (
            <div
              style={{
                position: 'absolute',
                left: 15,
                top: -8,
                bottom: 12,
                width: 2,
                background: '#e4e6eb',
                zIndex: 0,
              }}
            />
          )}

          {/* Toggle Replies / View Replies Button */}
          {repliesCount > 0 && !showReplies && (
            <div style={{ position: 'relative', padding: '6px 0 2px 0' }}>
              {/* Curve connector branching */}
              <div
                style={{
                  position: 'absolute',
                  left: -9,
                  top: 14,
                  width: 14,
                  height: 10,
                  borderBottom: '2px solid #e4e6eb',
                  borderLeft: '2px solid #e4e6eb',
                  borderBottomLeftRadius: 10,
                }}
              />
              <CommentActionBtn
                onClick={handleToggleReplies}
                style={{
                  color: '#65676b',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  paddingLeft: 12,
                }}>
                <MessageOutlined style={{ fontSize: 12 }} />
                Xem {repliesCount} phản hồi
              </CommentActionBtn>
            </div>
          )}

          {/* Replies list */}
          {showReplies && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                marginTop: 6,
              }}>
              {replies.map(reply => (
                <div key={reply.id} style={{ position: 'relative' }}>
                  {/* Left branch curve connector */}
                  <div
                    style={{
                      position: 'absolute',
                      left: -9,
                      top: 14,
                      width: 14,
                      height: 10,
                      borderBottom: '2px solid #e4e6eb',
                      borderLeft: '2px solid #e4e6eb',
                      borderBottomLeftRadius: 10,
                    }}
                  />
                  <CommentItem
                    comment={reply}
                    postId={postId}
                    currentUser={currentUser}
                    newsFeedStore={newsFeedStore}
                    authenticationStore={authenticationStore}
                    userStore={userStore}
                    onReply={target => {
                      setReplyTarget(target)
                      setIsReplying(true)
                    }}
                    depth={1}
                    dark={dark}
                  />
                </div>
              ))}

              {/* Loading indicator */}
              {repliesLoading && (
                <div style={{ padding: '4px 8px' }}>
                  <Spin size="small" />
                </div>
              )}

              {/* View more replies */}
              {hasMoreReplies && !repliesLoading && replies.length > 0 && (
                <CommentActionBtn
                  onClick={loadMoreReplies}
                  style={{
                    color: '#65676b',
                    alignSelf: 'flex-start',
                    fontSize: 12,
                    padding: '2px 0',
                    fontWeight: 600,
                  }}>
                  Xem thêm phản hồi...
                </CommentActionBtn>
              )}

              {/* Hide replies */}
              {replies.length > 0 && (
                <CommentActionBtn
                  onClick={handleToggleReplies}
                  style={{
                    color: '#65676b',
                    alignSelf: 'flex-start',
                    fontSize: 12,
                    padding: '2px 0',
                    fontWeight: 600,
                  }}>
                  Ẩn phản hồi
                </CommentActionBtn>
              )}
            </div>
          )}

          {/* Sub-reply inline input bar */}
          {isReplying && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                marginTop: 8,
                position: 'relative',
              }}>
              {/* Curve connector branching for input */}
              <div
                style={{
                  position: 'absolute',
                  left: -9,
                  top: 12,
                  width: 14,
                  height: 10,
                  borderBottom: '2px solid #e4e6eb',
                  borderLeft: '2px solid #e4e6eb',
                  borderBottomLeftRadius: 10,
                }}
              />

              {/* Current User Avatar */}
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  flexShrink: 0,
                  zIndex: 1,
                }}>
                {currentUserAvatarSrc ? (
                  <img
                    src={currentUserAvatarSrc}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      background: colors.primary,
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 'bold',
                    }}>
                    {currentUser?.display_name?.charAt(0) || 'U'}
                  </span>
                )}
              </div>

              {/* Sub-input Box */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}>
                <input
                  type="file"
                  ref={replyFileInputRef}
                  style={{ display: 'none' }}
                  accept="image/*,video/*"
                  onChange={handleReplyFileSelect}
                />

                {/* File Preview */}
                {replyAttachedFile && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'flex-start',
                      gap: 6,
                      padding: '2px 4px',
                      marginBottom: 2,
                    }}>
                    <div
                      style={{ position: 'relative', display: 'inline-block' }}>
                      {replyAttachedFile.type === 'image' ? (
                        <img
                          src={replyAttachedFile.previewUrl}
                          alt="preview"
                          style={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e2e8f0',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 60,
                            height: 60,
                            borderRadius: 8,
                            background: '#1e293b',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                          }}>
                          <video
                            src={replyAttachedFile.previewUrl}
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            muted
                          />
                          <PlayCircleFilled
                            style={{
                              color: '#fff',
                              fontSize: 20,
                              position: 'relative',
                              zIndex: 1,
                              opacity: 0.9,
                            }}
                          />
                        </div>
                      )}
                      <CloseCircleFilled
                        onClick={removeReplyAttachedFile}
                        style={{
                          position: 'absolute',
                          top: -6,
                          right: -6,
                          fontSize: 15,
                          color: '#64748b',
                          background: '#fff',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                        }}
                      />
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f0f2f5',
                    borderRadius: 16,
                    padding: '4px 12px',
                    border: '1px solid #e4e6eb',
                    position: 'relative',
                  }}>
                  {showEmojiPicker && (
                    <div
                      className="nf-reply-emoji-picker"
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        padding: 8,
                        width: 210,
                        zIndex: 1000,
                        marginBottom: 8,
                      }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(5, 1fr)',
                          gap: 6,
                        }}>
                        {COMMON_EMOJIS.map(emoji => (
                          <div
                            key={emoji}
                            onClick={() => insertEmoji(emoji)}
                            style={{
                              fontSize: 18,
                              textAlign: 'center',
                              cursor: 'pointer',
                              padding: 4,
                              borderRadius: 6,
                              transition: 'background 0.1s',
                              userSelect: 'none',
                            }}
                            onMouseEnter={e =>
                              (e.currentTarget.style.background = '#f1f5f9')
                            }
                            onMouseLeave={e =>
                              (e.currentTarget.style.background = 'transparent')
                            }>
                            {emoji}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ flex: 1, position: 'relative' }}>
                    <MentionsInput
                      className="nf-reply-mentions"
                      inputRef={replyInputRef}
                      value={replyText}
                      onChange={(e, newValue) => setReplyText(newValue)}
                      placeholder="Viết phản hồi..."
                      onKeyDown={e => {
                        if (e.defaultPrevented) return
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleSendReply()
                        }
                      }}
                      disabled={isSendingReply}
                      spellCheck={false}
                      allowSpaceInQuery={true}
                      style={{
                        control: {
                          fontSize: 13,
                          fontWeight: 'normal',
                          width: '100%',
                          fontFamily: 'inherit',
                          lineHeight: '18px',
                          minHeight: 18,
                        },
                        '&multiLine': {
                          control: { minHeight: 18 },
                          highlighter: {
                            padding: '3px 0',
                            border: 'none',
                            lineHeight: '18px',
                            fontSize: 13,
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            margin: 0,
                          },
                          input: {
                            padding: '3px 0',
                            border: 'none',
                            outline: 'none',
                            lineHeight: '18px',
                            fontSize: 13,
                            fontFamily: 'inherit',
                            boxSizing: 'border-box',
                            margin: 0,
                            background: 'transparent',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            resize: 'none',
                            overflow: 'hidden',
                          },
                        },
                        suggestions: {
                          list: {
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            fontSize: 13,
                            width: 250,
                            maxHeight: 200,
                            overflowY: 'auto',
                            zIndex: 1000,
                            position: 'absolute',
                            bottom: '100%',
                            marginBottom: 8,
                          },
                          item: {
                            padding: '8px 12px',
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer',
                            '&focused': {
                              backgroundColor: '#f1f5f9',
                            },
                          },
                        },
                      }}>
                      <Mention
                        className="nf-mention-tag"
                        appendSpaceOnAdd
                        trigger="@"
                        data={(query, callback) => {
                          const users = userStore?.userList || []
                          const removeAccents = str =>
                            str
                              ? str
                                  .normalize('NFD')
                                  .replace(/[\u0300-\u036f]/g, '')
                                  .replace(/đ/g, 'd')
                                  .replace(/Đ/g, 'D')
                              : ''
                          
                          let filtered = []
                          if (!query) {
                            filtered = users.slice(0, 10)
                          } else {
                            const lowerQuery = removeAccents(query.toLowerCase())
                            filtered = users
                              .filter(u => {
                                const display = u.display || u.name_uppercase || u.full_name || u.fullName || u.username || ''
                                const username = u.username || ''
                                return removeAccents(display.toLowerCase()).includes(lowerQuery) ||
                                       removeAccents(username.toLowerCase()).includes(lowerQuery)
                              })
                              .slice(0, 10)
                          }
                          
                          const mapped = filtered.map(u => ({
                            id: u.id || u.code || u.username,
                            display: u.display || u.name_uppercase || u.full_name || u.fullName || u.username,
                            image: u.imageId || u.image || u.userImage,
                            username: u.username,
                          }))
                          callback(mapped)
                        }}
                        markup="@[__display__]"
                        displayTransform={(id, display) => display}
                        style={{
                          backgroundColor: 'rgba(24, 119, 242, 0.12)',
                          color: '#1877f2',
                          borderRadius: 2,
                          lineHeight: '18px',
                          display: 'inline-block',
                        }}
                        renderSuggestion={(
                          suggestion,
                          search,
                          highlightedDisplay,
                          index,
                          focused
                        ) => (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              background: focused ? '#f1f5f9' : 'transparent',
                            }}>
                            {suggestion.image ? (
                              <img
                                src={
                                  suggestion.image.startsWith('http') || suggestion.image.startsWith('data:')
                                    ? suggestion.image
                                    : `${apiUrl}/api/v1/images/${suggestion.image}`
                                }
                                alt=""
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  background: '#2C65AC',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 11,
                                  fontWeight: 'bold',
                                }}>
                                {suggestion.display.charAt(0)}
                              </div>
                            )}
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                textAlign: 'left',
                              }}>
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: '#050505',
                                  lineHeight: 1.2,
                                }}>
                                {suggestion.display}
                              </span>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: '#65676b',
                                  lineHeight: 1.2,
                                }}>
                                @{suggestion.username || suggestion.id}
                              </span>
                            </div>
                          </div>
                        )}
                      />
                    </MentionsInput>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginLeft: 6,
                    }}>
                    <PictureFilled
                      onClick={openReplyFilePicker}
                      title="Đính kèm ảnh/video"
                      style={{
                        fontSize: 18,
                        lineHeight: 1,
                        color: replyAttachedFile ? colors.primary : '#8c939d',
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        display: 'flex',
                      }}
                    />
                    <SmileFilled
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      style={{
                        fontSize: 18,
                        lineHeight: 1,
                        color: showEmojiPicker ? '#f59e0b' : '#8c939d',
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        display: 'flex',
                      }}
                    />
                    <SendOutlined
                      onClick={
                        replyText.trim() || replyAttachedFile
                          ? handleSendReply
                          : undefined
                      }
                      style={{
                        fontSize: 18,
                        lineHeight: 1,
                        color:
                          replyText.trim() || replyAttachedFile
                            ? '#1877f2'
                            : '#bcc0c4',
                        cursor:
                          replyText.trim() || replyAttachedFile
                            ? 'pointer'
                            : 'default',
                        transition: 'color 0.2s',
                        display: 'flex',
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    paddingLeft: 4,
                    fontSize: 11,
                  }}>
                  <span
                    style={{
                      color: '#65676b',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                    onClick={() => {
                      setIsReplying(false)
                      setReplyText('')
                    }}>
                    Hủy
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <ReactionsModal
        visible={likesModalVisible}
        onCancel={() => setLikesModalVisible(false)}
        footer={null}
        width={450}
        bodyStyle={{ padding: '0', display: 'flex', flexDirection: 'column' }}
        destroyOnClose
        closable={true}
        title={null}>
        <div
          style={{
            padding: '12px 16px 0 16px',
            borderBottom: '1px solid #e4e6eb',
          }}>
          <Tabs
            activeKey={activeReactionTab}
            onChange={setActiveReactionTab}
            size="large"
            tabBarStyle={{ margin: 0, borderBottom: 'none' }}>
            <TabPane
              tab={
                <span style={{ fontWeight: 600 }}>
                  Tất cả {groupedReactions.ALL.length}
                </span>
              }
              key="ALL"
            />
            {Object.keys(groupedReactions)
              .filter(k => k !== 'ALL')
              .map(rKey => {
                const rObj = REACTIONS.find(r => r.key === rKey)
                if (!rObj) return null
                return (
                  <TabPane
                    tab={
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontWeight: 600,
                          color:
                            activeReactionTab === rKey ? rObj.color : undefined,
                        }}>
                        <ReactionIcon type={rKey} size={16} />{' '}
                        {groupedReactions[rKey].length}
                      </span>
                    }
                    key={rKey}
                  />
                )
              })}
          </Tabs>
        </div>
        <div
          className="reactions-modal-list"
          style={{ padding: '8px 16px', overflowY: 'auto', maxHeight: 400 }}>
          {loadingLikes ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '24px 0',
              }}>
              <Spin size="medium" />
            </div>
          ) : currentTabUsers.length === 0 ? (
            <div
              style={{ textAlign: 'center', color: '#888', padding: '24px 0' }}>
              Chưa có lượt thích nào.
            </div>
          ) : (
            <List
              dataSource={currentTabUsers}
              renderItem={item => {
                const displayName = formatName(
                  item.displayName || item.userCode
                )

                return (
                  <UserProfilePopover
                    userCode={item.userCode}
                    displayName={item.displayName}
                    avatarUrl={
                      item.avatar_url || item.avatarUrl || item.avatar
                    }>
                    <List.Item
                      style={{
                        borderBottom: 'none',
                        padding: '8px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        borderRadius: 8,
                        paddingLeft: 8,
                        paddingRight: 8,
                      }}
                      className="hoverable-list-item">
                      <div
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                        }}>
                        <Avatar
                          size={40}
                          src={resolveAvatarUrl(
                            item.avatar_url || item.avatarUrl || item.avatar
                          )}
                          style={{
                            background: colors.primary,
                            color: '#fff',
                            fontSize: 14,
                            fontWeight: 'bold',
                          }}>
                          {displayName.charAt(0)}
                        </Avatar>
                        <div
                          style={{
                            position: 'absolute',
                            bottom: -2,
                            right: -2,
                            borderRadius: '50%',
                            width: 18,
                            height: 18,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff',
                            border: '2px solid white',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                          }}>
                          <ReactionIcon
                            type={item.reactionObj?.key || 'LIKE'}
                            size={14}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                        }}>
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 15,
                            color: '#050505',
                          }}>
                          {displayName}
                        </span>
                        <span style={{ fontSize: 13, color: '#65676b' }}>
                          @{item.userCode}
                        </span>
                      </div>
                    </List.Item>
                  </UserProfilePopover>
                )
              }}
            />
          )}
        </div>
      </ReactionsModal>
    </CommentItemContainer>
  )
}

export default inject(
  'newsFeedStore',
  'authenticationStore',
  'userStore'
)(observer(CommentItem))
